import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Clickable, KpiSub, KpiValue, Sub, Table } from '../../performance-operations/styles';
import { formatNumber, hasValue, metricOf } from '../../performance-operations/config';
import { IDP_STATUSES, calendarRows, unitChartRows } from '../config';

// Analiz E12 — gelişim planı sekmesi panelleri: KPI (M33 kapsama, M34 aksiyon tamamlama, M35 geciken aksiyon), plan
// durumlarına göre yığılmış sütun (birim başına), aksiyonların son tarih takvimi ve kural tabanlı dikkat bulguları. Her grafiğin
// tablo alternatifi var; grafik/tablo tıklaması alttaki plan listesine süzgeç ekler.
const statusLabel = (t, s) => t(`performance.idp.status.${s}`, { defaultValue: s });

export function Kpi({ code, title, metric, sub, unit = 'percent', loading, error, onRetry }) {
  const { t, i18n } = useTranslation();
  let value = t('performance.operations.kpi.noData');
  if (hasValue(metric) || (unit !== 'percent' && metric?.value != null)) {
    const n = formatNumber(metric.value, i18n.language, unit === 'count' ? 0 : 2);
    value = unit === 'percent' ? `${n}%` : n;
  }
  const ratio = unit === 'percent' && hasValue(metric)
    ? t('performance.operations.kpi.ratio', { num: formatNumber(metric.numerator, i18n.language, 0), den: formatNumber(metric.denominator, i18n.language, 0) })
    : null;
  return (
    <PanelCard panelKey={`kpi-${code}`} title={title} span={3} kpi compact loading={loading} error={error} onRetry={onRetry}>
      <KpiValue data-kpi={code}>{value}</KpiValue>
      {ratio ? <KpiSub>{ratio}</KpiSub> : null}
      {sub ? <KpiSub>{sub}</KpiSub> : null}
    </PanelCard>
  );
}

export function DevelopmentKpis({ panel }) {
  const { t, i18n } = useTranslation();
  const d = panel.data;
  const common = { loading: panel.loading, error: panel.error, onRetry: panel.retry };
  const m33 = metricOf(d?.metrics, 'M33');
  return (
    <>
      <Kpi
        code="M33" title={t('performance.developmentAnalytics.kpi.coverage')} metric={m33}
        sub={d && d.requiredN === 0 ? t('performance.developmentAnalytics.kpi.noRequirement') : t('performance.developmentAnalytics.kpi.coverageSub')}
        {...common}
      />
      <Kpi code="M34" title={t('performance.developmentAnalytics.kpi.actionCompletion')} metric={metricOf(d?.metrics, 'M34')} sub={t('performance.developmentAnalytics.kpi.actionCompletionSub')} {...common} />
      <Kpi code="M35" title={t('performance.developmentAnalytics.kpi.overdueActions')} metric={metricOf(d?.metrics, 'M35')} unit="count" sub={t('performance.developmentAnalytics.kpi.overdueActionsSub')} {...common} />
      <Kpi
        code="planHolders" title={t('performance.developmentAnalytics.kpi.planHolders')}
        metric={d ? { value: d.planHolders } : null} unit="count"
        sub={d ? t('performance.developmentAnalytics.kpi.planHoldersSub', { n: formatNumber(d.requiredN, i18n.language, 0) }) : null}
        {...common}
      />
    </>
  );
}

export function PlanStatusPanel({ panel, onPickStatus }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const rows = unitChartRows(t, panel.data?.statusByUnit);
  const palette = [
    theme.colors.text.muted, theme.colors.auxiliar.purple, theme.colors.auxiliar.warning, theme.colors.auxiliar.danger,
    theme.colors.primary.main, theme.colors.primary.dark, theme.colors.secondary.main, theme.colors.text.light.medium,
  ];
  const statuses = IDP_STATUSES.filter((s) => rows.some((r) => r[s] > 0));
  return (
    <PanelCard
      panelKey="planStatus" title={t('performance.developmentAnalytics.panel.planStatus')} span={8}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={rows.length === 0}
      emptyText={t('performance.developmentAnalytics.noPlans')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t('performance.developmentAnalytics.col.unit')}</th>
              {statuses.map((s) => <th key={s}>{statusLabel(t, s)}</th>)}
              <th>{t('performance.developmentAnalytics.col.total')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-row={r.key}>
                <td>{r.label}</td>
                {statuses.map((s) => (
                  <td key={s}>
                    {r[s] > 0 ? <Clickable type="button" data-pick={s} onClick={() => onPickStatus(s)}>{r[s]}</Clickable> : 0}
                  </td>
                ))}
                <td>{r.total}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: theme.metrics.font.size.xs }} interval="preserveStartEnd" />
              <YAxis allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
              {statuses.map((s) => (
                <Bar
                  key={s} dataKey={s} name={statusLabel(t, s)} stackId="plans" cursor="pointer"
                  fill={palette[IDP_STATUSES.indexOf(s) % palette.length]} onClick={() => onPickStatus(s)}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}

export function DueCalendarPanel({ panel, onPickOverdue }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [table, setTable] = useState(false);
  const rows = calendarRows(t, panel.data?.dueCalendar, i18n.language);
  const empty = rows.every((r) => r.count === 0);
  return (
    <PanelCard
      panelKey="dueCalendar" title={t('performance.developmentAnalytics.panel.dueCalendar')} span={4}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={empty}
      emptyText={t('performance.developmentAnalytics.noOpenActions')}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {table ? (
        <Table>
          <thead><tr><th>{t('performance.developmentAnalytics.col.due')}</th><th>{t('performance.developmentAnalytics.col.actions')}</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} data-row={r.key}>
                <td>
                  {r.key === 'overdue' && r.count > 0
                    ? <Clickable type="button" data-pick="overdue" onClick={onPickOverdue}>{r.label}</Clickable>
                    : r.label}
                </td>
                <td>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <BarChart data={rows} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: theme.metrics.font.size.xs }} interval={0} />
              <YAxis allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip />
              <Bar
                dataKey="count" name={t('performance.developmentAnalytics.col.actions')} fill={theme.colors.primary.main}
                cursor="pointer" onClick={(d) => { if ((d.payload || d).key === 'overdue') onPickOverdue(); }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}

export function DevelopmentAttentionPanel({ panel, onPickStatus, onPickOverdue }) {
  const { t, i18n } = useTranslation();
  const a = panel.data?.attention || {};
  const n = (v) => formatNumber(v || 0, i18n.language, 0);
  return (
    <PanelCard
      panelKey="devAttention" title={t('performance.developmentAnalytics.panel.attention')} span={4}
      loading={panel.loading} error={panel.error} onRetry={panel.retry}
    >
      <Table>
        <tbody>
          <tr data-row="meetingPending">
            <td><Clickable type="button" onClick={() => onPickStatus('meeting')}>{t('performance.developmentAnalytics.attention.meetingPending')}</Clickable></td>
            <td>{n(a.meetingPending)}</td>
          </tr>
          <tr data-row="reviewPending">
            <td><Clickable type="button" onClick={() => onPickStatus('period_end_review')}>{t('performance.developmentAnalytics.attention.reviewPending')}</Clickable></td>
            <td>{n(a.reviewPending)}</td>
          </tr>
          <tr data-row="completedWithoutEvidence">
            <td>{t('performance.developmentAnalytics.attention.completedWithoutEvidence')}</td>
            <td>{n(a.completedWithoutEvidence)}</td>
          </tr>
          <tr data-row="openActionsInClosedPlans">
            <td>{t('performance.developmentAnalytics.attention.openActionsInClosedPlans')}</td>
            <td>{n(a.openActionsInClosedPlans)}</td>
          </tr>
          <tr data-row="overdue">
            <td><Clickable type="button" onClick={onPickOverdue}>{t('performance.developmentAnalytics.attention.overdue')}</Clickable></td>
            <td>{n(metricOf(panel.data?.metrics, 'M35')?.value)}</td>
          </tr>
        </tbody>
      </Table>
      <Sub>{t('performance.developmentAnalytics.attention.disclaimer')}</Sub>
    </PanelCard>
  );
}
